import { Box, Card, CardContent, Divider, Grid2, Icon } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'

import TabHeader from '@/app/component/atom/tab-header'
import CustomTooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import AccessWrapper from '@/app/component/wrapper/access'
import { useGetBalance } from '@/app/module/payments/hooks/use-get-balance'
import { useGetCreditCards } from '@/app/module/payments/hooks/use-get-credit-cards'
import { FUNDS_TRANSFER_ROLES, PAYMENT_ROLES } from '@/app/module/payments/roles'
import { selectOrgs, selectUserRole } from '@/app/module/user/store/selectors'
import { useRequirePermission } from '@/app/module/user/store/ts/hooks'
import { Organization, UserRole } from '@/app/types'

import Payment from './payment'
import ProformaInvoice from './proforma/proforma-invoice-dialog'
import TransferFundsDialog from './transfer/transfer-funds-dialog'
import { getDisabledCreditCardReason } from './utils'

const Overview: React.FC = () => {
  const organizations = useSelector(selectOrgs) as Organization[]
  const role = useSelector(selectUserRole) as UserRole

  const { data: balance } = useGetBalance()
  const { data: cards } = useGetCreditCards()

  const [proformaModal, setProformaModal] = React.useState(false)
  const [transferModal, setTransferModal] = React.useState(false)

  const canTransferFund = !!organizations?.length && FUNDS_TRANSFER_ROLES.includes(role)
  const hasCCPermission = useRequirePermission('creditCard')
  const hasBillingRoles = PAYMENT_ROLES.includes(role)
  const canMakePayment = hasBillingRoles && hasCCPermission

  return (
    <>
      {proformaModal && (
        <ProformaInvoice
          onClose={() => {
            setProformaModal(false)
          }}
        />
      )}
      {transferModal && <TransferFundsDialog open={transferModal} onClose={() => setTransferModal(false)} />}

      <Box>
        <Grid2 container spacing={2}>
          <Grid2 size={{ md: 8, xs: 12 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card>
              <TabHeader
                action={
                  canTransferFund && (
                    <CustomTooltip title="Transfer funds to another organization">
                      <HeaderButton
                        disabled={!canTransferFund}
                        variant="contained"
                        color="primary"
                        onClick={() => setTransferModal(true)}
                      >
                        Transfer funds
                      </HeaderButton>
                    </CustomTooltip>
                  )
                }
                title="Current Balance"
              />
              <CardContent>
                <Typography variant="h3" color="primary">
                  ${balance?.balanceHuman}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <AccessWrapper
                fallback={
                  <Typography component="div" variant="h6">
                    {getDisabledCreditCardReason(hasBillingRoles, hasCCPermission)}
                  </Typography>
                }
                hasAccess={canMakePayment}
              >
                <TabHeader
                  action={
                    <HeaderButton variant="contained" color="primary" onClick={() => setProformaModal(true)}>
                      Create a Proforma Invoice
                    </HeaderButton>
                  }
                  title={!cards?.length ? 'Add Credit Card' : 'Add Funds'}
                />
                <Box px={2}>
                  <IconText>
                    <Icon fontSize="small" style={{ color: '#5BC236' }}>
                      lock_outline
                    </Icon>
                    <Typography variant="caption">
                      {"This is a secure 256-bit SSL Encrypted payment. You're safe."}
                    </Typography>
                  </IconText>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Payment />
              </AccessWrapper>
            </Card>
          </Grid2>
        </Grid2>
      </Box>
    </>
  )
}

const HeaderButton = styled(Button)({
  marginLeft: '10px',
})

export default Overview
